/** @jsx jsx */
import * as Sentry from '@sentry/browser'
import { Form, Formik, FormikHelpers } from 'formik'
import React, { useState, useEffect } from 'react'
import { Flex, jsx } from 'theme-ui'
import * as Yup from 'yup'
import {
  Box,
  Button,
  Collapse,
  FormGroupInput,
  FormGroupLabel,
  LoadingPlaceholder,
  Col,
  Row,
  Heading,
  Divider,
} from '../../components'
import { Card, lastCardContent } from '../../components/Card'
import { ConnectedFormGroup, ConnectedFormGroupDivider } from '../../components/ConnectedFormField'
import { FormErrorMessage, GeneralFormErrorMessage } from '../../components/FormErrorMessage'
import { Model } from '../../sharedBetweenServerAndClient__DO_NOT_EDIT/types'
import firebase from 'gatsby-plugin-firebase'
import { getFunction } from '../../utils/api'
import { Modal } from '../../components/Modal'
import { ButtonBlock } from '../../components/ButtonBlock'
import { PulsingText } from '../../components/PulsingText'
import { captureExceptionWithLabel } from '../../utils/sentry'

interface FormValues {
  initialChipsAmount: string
  response: 'accept' | 'reject'
}

export const validationSchema = Yup.object().shape({
  initialChipsAmount: Yup.number()
    .integer('Initial chips must be between 100 and 100,000')
    .min(100, 'Initial chips must be between 100 and 100,000')
    .max(100000, 'Initial chips must be between 100 and 100,000'),
})

const PendingUserRequestRow: React.SFC<{ game: Model.Game; userEmail: string }> = ({
  game,
  userEmail,
  ...props
}) => {
  const onSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>) => {
    actions.setSubmitting(true)
    try {
      await getFunction('respondToGameAccessRequest')({
        gameId: game.id,
        initialChipsAmount: parseInt(values.initialChipsAmount, 10),
        response: values.response,
        userEmail,
      })
      actions.setSubmitting(false)
    } catch (error) {
      captureExceptionWithLabel(error, 'PendingUserRequestRow submit')
      actions.setFieldError(
        'generalError',
        "Whoops! Looks like we have some issues. We're working on a fix",
      )
      actions.setSubmitting(false)
    }
  }
  return (
    <Formik<FormValues>
      initialValues={{
        initialChipsAmount: `${game.initialRules.initialChipsAmount}`,
        response: 'accept',
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formProps) => {
        return (
          <Form noValidate {...props}>
            <Row sx={{ alignItems: 'center' }}>
              <Col xs={12} md={4}>
                {userEmail}
              </Col>
              <Col xs={6} md={4}>
                <ConnectedFormGroup sx={{ py: 2 }} name={'initialChipsAmount'}>
                  {({ field, hasErrors }) => (
                    <React.Fragment>
                      <FormGroupLabel sx={{ py: 2 }} htmlFor={field.name}>
                        Initial Chips #
                      </FormGroupLabel>
                      <ConnectedFormGroupDivider />
                      <FormGroupInput
                        type="number"
                        hasErrors={hasErrors}
                        id={field.name}
                        sx={{ ...lastCardContent, py: 2 }}
                        {...field}
                      />
                    </React.Fragment>
                  )}
                </ConnectedFormGroup>
              </Col>
              <Col xs={6} md={4}>
                <Button block size="sm" type="submit" disabled={formProps.isSubmitting}>
                  <LoadingPlaceholder loadingVariant="light" loading={formProps.isSubmitting}>
                    Approve
                  </LoadingPlaceholder>
                </Button>

                <Button
                  sx={{ color: 'red', borderColor: 'transparent', backgroundColor: 'white', mt: 3 }}
                  block
                  size="sm"
                  disabled={formProps.isSubmitting}
                  onClick={() => {
                    formProps.setFieldValue('response', 'reject')
                    formProps.submitForm()
                  }}
                >
                  <LoadingPlaceholder loadingVariant="dark" loading={formProps.isSubmitting}>
                    Reject
                  </LoadingPlaceholder>
                </Button>
              </Col>
            </Row>
            <FormErrorMessage sx={{ mt: 2 }} name="initialChipsAmount" />
            <GeneralFormErrorMessage sx={{ mt: 2 }} />
          </Form>
        )
      }}
    </Formik>
  )
}

export const PendingUserRequestButton: React.SFC<
  { game: Model.Game } & React.ComponentPropsWithoutRef<typeof Button>
> = ({ game, ...props }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  useEffect(() => {
    if (!game.pendingUserEmailAccessRequests.length && isModalOpen) {
      setIsModalOpen(false)
    }
  }, [game.pendingUserEmailAccessRequests.length])
  return (
    <React.Fragment>
      <Button
        onClick={() => setIsModalOpen(true)}
        children={
          <PulsingText>
            {game.pendingUserEmailAccessRequests.length} Pending User Requests
          </PulsingText>
        }
        {...props}
      />
      <Modal
        label="Pending User Requests"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        header={<Heading as="h3">Pending User Requests</Heading>}
      >
        {game.pendingUserEmailAccessRequests.map((userEmail, index) => (
          <React.Fragment key={userEmail}>
            {!!index && <Divider sx={{ m: [4, 4, 5] }} />}
            <PendingUserRequestRow key={userEmail} {...{ game, userEmail }} />
          </React.Fragment>
        ))}
      </Modal>
    </React.Fragment>
  )
}
